import { useMemo, useState } from 'react';
import { useStore } from '../store';
import type { ImageInfo, Page } from '../types';

// alt 属性に「問題あり」と扱うかの判定。
// 厳密には alt="" は「装飾画像として意図的」な使い方もあるが、実務では
// 「alt 書き忘れ」として指摘されることが多い。両方を「要確認」扱いとする。
function isMissingAlt(img: ImageInfo): boolean {
  return img.alt === undefined || img.alt === '';
}

export function ImagesView() {
  const data = useStore((s) => s.data);
  const [filter, setFilter] = useState('');
  const [onlyMissingAlt, setOnlyMissingAlt] = useState(false);

  // images が一切記録されていないかチェック（旧 JSON / --no-images / --slim 対策）
  const noImagesAtAll = useMemo(() => {
    if (!data) return true;
    return data.pages.every((p) => p.images === undefined);
  }, [data]);

  // ページ別グループ表示用のデータ
  const grouped = useMemo(() => {
    if (!data) return [];
    const f = filter.trim().toLowerCase();

    return data.pages
      .map((page) => {
        const images = page.images ?? [];
        const filteredImages = images.filter((img) => {
          if (onlyMissingAlt && !isMissingAlt(img)) return false;
          if (f) {
            const inSrc = img.src.toLowerCase().includes(f);
            const inAlt = (img.alt ?? '').toLowerCase().includes(f);
            if (!inSrc && !inAlt) return false;
          }
          return true;
        });
        return {
          page,
          images: filteredImages,
          missingAltCount: images.filter(isMissingAlt).length,
          totalImages: images.length,
        };
      })
      .filter((g) => g.images.length > 0);
  }, [data, filter, onlyMissingAlt]);

  // 全体集計
  const totals = useMemo(() => {
    if (!data) {
      return { totalImages: 0, missingAlt: 0, altUndefined: 0, altEmpty: 0, pagesWithImages: 0 };
    }
    let totalImages = 0;
    let missingAlt = 0;
    let altUndefined = 0;
    let altEmpty = 0;
    let pagesWithImages = 0;
    for (const p of data.pages) {
      const images = p.images ?? [];
      if (images.length > 0) pagesWithImages++;
      totalImages += images.length;
      for (const img of images) {
        if (img.alt === undefined) altUndefined++;
        else if (img.alt === '') altEmpty++;
        if (isMissingAlt(img)) missingAlt++;
      }
    }
    return { totalImages, missingAlt, altUndefined, altEmpty, pagesWithImages };
  }, [data]);

  if (!data) return <></>;

  if (noImagesAtAll) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <p className="text-sm text-slate-700">画像情報が記録されていません</p>
        <p className="mt-2 text-xs text-slate-500">
          以下の理由が考えられます:
        </p>
        <ul className="mt-1 inline-block text-left text-xs text-slate-500">
          <li>・ <code>--no-images</code> または <code>--slim</code> オプションで省略された</li>
          <li>・ 古いバージョン (v0.1.x) の JSON で画像情報が含まれていない</li>
        </ul>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-sm text-slate-700">
          画像 <span className="font-semibold">{totals.totalImages}</span> 枚 · alt
          要確認{' '}
          <span
            className={
              totals.missingAlt > 0
                ? 'font-semibold text-amber-700'
                : 'font-semibold text-slate-700'
            }
            title={`alt 属性なし: ${totals.altUndefined} 枚 / alt='' (空文字): ${totals.altEmpty} 枚`}
          >
            {totals.missingAlt}
          </span>{' '}
          枚 (alt 属性なし {totals.altUndefined} / alt='' {totals.altEmpty}) · 全{' '}
          <span className="font-semibold">{totals.pagesWithImages}</span>{' '}
          ページに点在
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={onlyMissingAlt}
              onChange={(e) => setOnlyMissingAlt(e.target.checked)}
              className="rounded"
            />
            alt 要確認のみ表示
          </label>
          <input
            type="search"
            placeholder="src または alt でフィルタ"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-56 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>

      {grouped.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          {onlyMissingAlt
            ? '要確認の alt はありません（属性なし・空文字ともに 0 件）'
            : '該当する画像はありません'}
        </div>
      ) : (
        <ul className="space-y-3">
          {grouped.map(({ page, images, missingAltCount, totalImages }) => (
            <PageGroup
              key={page.url}
              page={page}
              images={images}
              missingAltCount={missingAltCount}
              totalImages={totalImages}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function PageGroup({
  page,
  images,
  missingAltCount,
  totalImages,
}: {
  page: Page;
  images: ImageInfo[];
  missingAltCount: number;
  totalImages: number;
}) {
  return (
    <li className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs">
        <a
          href={page.url}
          target="_blank"
          rel="noreferrer"
          className="truncate font-mono text-slate-700 hover:text-blue-600 hover:underline"
        >
          {page.url}
        </a>
        <span className="shrink-0 text-slate-500">
          画像 {totalImages} 枚
          {missingAltCount > 0 && (
            <span className="ml-2 text-amber-700">
              · alt 無し {missingAltCount} 枚
            </span>
          )}
        </span>
      </div>
      <table className="w-full text-xs">
        <thead className="border-b border-slate-100 bg-slate-50/50 text-[10px] uppercase text-slate-500">
          <tr>
            <th className="w-8 px-3 py-2 text-left">alt</th>
            <th className="px-3 py-2 text-left">src</th>
            <th className="px-3 py-2 text-left">alt テキスト</th>
            <th className="px-3 py-2 text-right">サイズ</th>
            <th className="px-3 py-2 text-right">loading</th>
          </tr>
        </thead>
        <tbody>
          {images.map((img, idx) => {
            const missing = isMissingAlt(img);
            return (
              <tr
                key={`${img.src}-${idx}`}
                className={`border-b border-slate-100 last:border-0 ${
                  missing ? 'bg-amber-50/50' : ''
                }`}
              >
                <td className="px-3 py-1.5 text-center text-base">
                  {missing ? (
                    <span title="alt 属性が指定されていません" className="text-amber-700">
                      ⚠
                    </span>
                  ) : (
                    <span title="alt 属性あり" className="text-emerald-600">
                      ✓
                    </span>
                  )}
                </td>
                <td className="max-w-md truncate px-3 py-1.5 font-mono text-slate-700">
                  <a
                    href={img.src}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-blue-600 hover:underline"
                  >
                    {img.src}
                  </a>
                </td>
                <td className="max-w-sm truncate px-3 py-1.5 text-slate-600">
                  {img.alt === undefined ? (
                    <span className="italic text-slate-400">(未指定)</span>
                  ) : img.alt === '' ? (
                    <span className="italic text-slate-400">(空文字 - 装飾画像)</span>
                  ) : (
                    img.alt
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                  {img.width && img.height
                    ? `${img.width}×${img.height}`
                    : '-'}
                </td>
                <td className="px-3 py-1.5 text-right text-slate-500">
                  {img.loading ?? '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </li>
  );
}
